import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, startWith } from 'rxjs';

import { SelectComponent } from '@blaze-shared/shared-feature';
import { ButtonIconComponent } from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { MappingOption } from '../models/data-connector-dialog';

interface FormMapping {
  fieldId: FormControl<any>;
  columnId: FormControl<any>;
}

@UntilDestroy()
@Component({
  selector: 'blaze-mapping-row',
  templateUrl: './mapping-row.component.html',
  styleUrls: ['./mapping-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectComponent, ReactiveFormsModule, AsyncPipe, IconsModule, ButtonIconComponent],
})
export class MappingRowComponent<
  T extends O[],
  V extends string = string,
  C extends string = string,
  O extends MappingOption<V, C> = MappingOption<V, C>,
> implements OnInit
{
  remove = output<void>();

  @Input() canRemove = false;
  @Input() form!: FormGroup<FormMapping>;

  availableMappings$$ = new BehaviorSubject<T>([] as any);
  selectedMapping$$ = new BehaviorSubject<O | undefined>(undefined);

  availableMappings$ = this.availableMappings$$.asObservable();
  selectedMapping$ = this.selectedMapping$$.asObservable();

  @Input() set availableMappings(value: T | undefined) {
    this.availableMappings$$.next((value ?? []) as any);
  }

  ngOnInit(): void {
    combineLatest({
      selectedFieldId: this.form.valueChanges.pipe(
        startWith(this.form.value),
        map((value) => value.fieldId),
        distinctUntilChanged(),
      ),
      availableMappings: this.availableMappings$$,
    })
      .pipe(
        untilDestroyed(this),
        map((data, index) => ({ ...data, isFirst: index === 0 })),
      )
      .subscribe(({ selectedFieldId, availableMappings, isFirst }) => {
        this.selectedMapping$$.next(
          availableMappings.find((mapping) => mapping.value === selectedFieldId),
        );

        if (!isFirst) {
          this.form.patchValue({ columnId: undefined });
        }
      });
  }
}
